import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import siteConfig from "@/config/site.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/aviso-legal")({
  component: LegalNoticePage,
  head: () => ({
    meta: [
      { title: `Legal Notice — ${siteConfig.brand.name}` },
      {
        name: "description",
        content:
          "Legal notice and general information for PULSO33: site owner, terms of use and intellectual property.",
      },
      { property: "og:title", content: `Legal Notice — ${siteConfig.brand.name}` },
      {
        property: "og:description",
        content: "Legal notice and general information for PULSO33.",
      },
      { property: "og:url", content: "https://pulso33.com/aviso-legal" },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/aviso-legal" }],
  }),
});

function LegalNoticePage() {
  const { tk } = useT();
  const office = siteConfig.addresses[0];
  const brand = siteConfig.brand.name;
  const email = siteConfig.contact.emails[0].address;

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">
          {tk("legal.kicker", "Legal")}
        </p>
        <h1 className="text-4xl md:text-5xl font-serif mb-8">
          {tk("legal.notice.title", "Legal Notice")}
        </h1>
        <div className="gold-divider mb-10" />

        <div className="space-y-6 text-foreground/80 leading-relaxed">
          <h2 className="font-serif text-2xl text-foreground mt-2">
            {tk("legal.notice.s1.t", "1. Site owner")}
          </h2>
          <p>
            {tk(
              "legal.notice.s1.a",
              "This website is owned and operated by ",
            )}
            {brand}
            {tk("legal.notice.s1.b", ", a distributed engineering team (")}
            {office.line}
            {tk("legal.notice.s1.c", "). You can reach us at ")}
            <a className="text-gold hover:underline" href={`mailto:${email}`}>
              {email}
            </a>
            .
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.notice.s2.t", "2. Purpose")}
          </h2>
          <p>
            {tk("legal.notice.s2.a", "This legal notice governs the use of the ")}
            {brand}
            {tk(
              "legal.notice.s2.b",
              " website, whose purpose is to present the IT services we offer.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.notice.s3.t", "3. Terms of use")}
          </h2>
          <p>
            {tk(
              "legal.notice.s3.d",
              "Users agree to make appropriate use of the content and services offered on this website, and not to use them for illegal activities or activities contrary to good faith and applicable law.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.notice.s4.t", "4. Intellectual property")}
          </h2>
          <p>
            {tk(
              "legal.notice.s4.a",
              "All content on this site (text, images, brand, logos and design) is the property of ",
            )}
            {brand}
            {tk(
              "legal.notice.s4.b",
              " or its respective owners and is protected by intellectual and industrial property law.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.notice.s5.t", "5. Liability")}
          </h2>
          <p>
            {brand}
            {tk(
              "legal.notice.s5.d",
              " is not liable for any damages arising from interferences, omissions, interruptions, computer viruses, faults or disconnections in the electronic system that are beyond our control.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.notice.s6.t", "6. Applicable law")}
          </h2>
          <p>
            {tk(
              "legal.notice.s6.d",
              "This legal notice is governed by Spanish law. For any dispute, the parties submit to the courts of the place of business, unless the law imposes another jurisdiction.",
            )}
          </p>
        </div>
      </article>
    </Layout>
  );
}
